import { useEffect, useMemo, useState } from 'react';
import { Modal, Table, Button, Form, Alert, Spinner, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTriangleExclamation,
  faObjectGroup,
  faBan,
  faRightLeft,
  faArrowRightArrowLeft,
  faScissors
} from '@fortawesome/free-solid-svg-icons';

import { http } from '../../features/jobs/api/http';
import {
  lineDrumsOf,
  handoverTargets,
  handoverItems,
  handoverIssue,
  overrunOf,
  asMetres
} from '../../helpers/drumHandover';

// DRUM SURGERY — the drums of one run, and the two repairs the ledger cannot make.
//
// The ledger re-states LENGTHS. It assumes the set of drums is right and only
// the boundaries between them are wrong, which holds for conductor_isolation
// and breaks everywhere else:
//
//   · a phase that consumes an input drum — moving metres between outputs
//     without giving the input back leaves the rope drum short forever
//   · one input, several outputs — the plan said three drums, the operator
//     wound two. The third never existed. There is no length to re-state,
//     because there is no drum.
//
// And a third case that is neither: the drum is real and its length is right,
// it simply belongs to a different product. That is REASSIGN. If the run
// carries other drums it is split — they stay where they are.
//
// So: CANCEL a drum that was never made, and MERGE drums that turned out to be
// one bobbin. Both are refused rather than fudged when something downstream has
// already drawn on them — see the last column.
//
// And SPLIT, which is merge read backwards: one token that is two physical
// drums. That is what an undetected drum change leaves — the machine does not
// stop when the pinoles open, so it carries on winding the next drum onto the
// same open token. A skip2250 rope drum recorded 6344.6 m against 4050 planned
// across a 35 h window with 4 h of running in it: one record, two bobbins. A
// drum that has not been made yet splits its PLAN instead, and the second half
// is queued straight behind the first.
//
// Scope is deliberately one task. Merging across tasks would mean one bobbin
// produced by two separate runs, which is not something a machine can do.
const n0 = v => (Number(v) || 0).toLocaleString('ro-RO');

/**
 * `initial` — open on a particular repair, already filled in.
 *
 * The Drum Ledger knows the answer before this opens: the planner has typed
 * what the drum really held, and the ledger has worked out that the rest has no
 * drum to go to. Making them pick the mode, find the row again and retype the
 * number is three chances to get it wrong on a screen that already knew.
 *
 * `{ mode, tokenId, keepLength }`.
 */
const DrumSurgeryModal = ({
  show,
  onHide,
  jobId,
  taskId,
  machineId,
  // The machine's queue, as the page already has it. Only the HANDOVER needs it:
  // its target is another DRUM on this line, and a list of products cannot name
  // one.
  queueRows = [],
  initial = null,
  onApplied
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState('');

  // 'cancel' | 'merge' | 'split' | 'reassign' | 'handover'
  const [mode, setMode] = useState('cancel');
  const [targetJobId, setTargetJobId] = useState('');
  // HANDOVER: which drum on this line the metres belong to, and how many.
  const [targetDrumKey, setTargetDrumKey] = useState('');
  const [handAmount, setHandAmount] = useState('');
  // The product list is every job in the factory. With a few hundred of them,
  // and many sharing a label, scrolling it is not a way to find anything.
  const [productFilter, setProductFilter] = useState('');
  // How much stays on the FIRST drum. Everything past it becomes the second.
  const [keepLength, setKeepLength] = useState('');
  // Which product pays for the input drum this one was made from. There is no
  // right default, so the default is the one that changes nothing.
  const [inputPolicy, setInputPolicy] = useState('keep');
  const [products, setProducts] = useState([]);
  const [picked, setPicked] = useState(() => new Set());
  const [survivor, setSurvivor] = useState('');
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState(null);

  const reset = () => {
    setPicked(new Set());
    setSurvivor('');
    setTargetJobId('');
    setTargetDrumKey('');
    setHandAmount('');
    setProductFilter('');
    setInputPolicy('keep');
    setKeepLength('');
    setPreview(null);
    setErr('');
    setDone('');
  };

  /**
   * `preset` is applied AFTER the reset, which is the only order that works —
   * reset is what clears the previous operation.
   */
  const load = async (preset = null) => {
    if (!jobId || !taskId) return;
    setLoading(true);
    setErr('');
    try {
      const { data: d } = await http.get(
        `/api/redis/jobs/${encodeURIComponent(jobId)}/tasks/${encodeURIComponent(taskId)}/drums`
      );
      setData(d);
      reset();
      if (preset?.mode) setMode(preset.mode);
      if (preset?.tokenId) setPicked(new Set([String(preset.tokenId)]));
      if (preset?.keepLength != null && preset.keepLength !== '') {
        setKeepLength(String(preset.keepLength));
      }
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Could not load the drums of this run.');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) load(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, jobId, taskId]);

  // Every product, not only the ones on this screen. A drum is usually moved to
  // a product that ran on another day, and a list built from the drums in view
  // cannot offer those.
  useEffect(() => {
    if (!show) return;
    let alive = true;
    http
      .get('/api/redis/jobs')
      .then(({ data: d }) => {
        if (!alive) return;
        const rows = Array.isArray(d) ? d : d?.jobs || d?.data || [];
        setProducts(
          rows
            .map(j => ({
              jobId: String(j.jobId || j._id || ''),
              productLabel: j.productLabel || j.jobCode || j.jobId,
              // Two lots of one product are the normal case and the label does
              // not separate them.
              plannedLength: Number(j.plannedLength) || 0
            }))
            .filter(j => j.jobId && j.jobId !== String(jobId))
        );
      })
      .catch(() => setProducts([]));
    return () => {
      alive = false;
    };
  }, [show, jobId]);

  // Memoised because the two useMemos below depend on it, and `|| []` is a new
  // array identity on every render.
  const rows = useMemo(() => data?.rows || [], [data]);

  // The machine's queue, for the handover's target list.
  //
  // Supplied by the screens that already have it, fetched here when not. THREE
  // screens open this modal — the queue table, the queue editor and the drum
  // ledger — and on 2026-10-22 only one of them was passing it, so the handover
  // reported "no other drum of this phase is queued on L70" with three of them
  // sitting on it. A prop every caller must remember is a prop one of them will
  // not, and this one is not optional to the feature.
  const [fetchedQueue, setFetchedQueue] = useState([]);
  const haveQueueRows = (queueRows || []).length > 0;

  useEffect(() => {
    if (!show || !machineId || haveQueueRows) return;
    let alive = true;
    http
      .get(`/api/redis/queues/${encodeURIComponent(machineId)}`)
      .then(({ data: d }) => {
        if (alive) setFetchedQueue(Array.isArray(d?.groups) ? d.groups : []);
      })
      .catch(() => setFetchedQueue([]));
    return () => {
      alive = false;
    };
    // `haveQueueRows` and not `queueRows`: the default `[]` is a new identity on
    // every render, and depending on it would re-fetch for ever.
  }, [show, machineId, haveQueueRows]);

  const queue = haveQueueRows ? queueRows : fetchedQueue;

  const toggle = tokenId => {
    setPreview(null);
    setDone('');
    setKeepLength('');
    setHandAmount('');
    setPicked(prev => {
      if (mode === 'reassign' || mode === 'split' || mode === 'handover') {
        // One drum at a time: the target product, the input decision and the
        // cut length are all per drum, and a batch would hide that they differ.
        return prev.has(tokenId) ? new Set() : new Set([tokenId]);
      }
      const next = new Set(prev);
      if (next.has(tokenId)) next.delete(tokenId);
      else next.add(tokenId);
      return next;
    });
  };

  // The kept bobbin cannot also be one of the absorbed ones.
  const chooseSurvivor = tokenId => {
    setPreview(null);
    setDone('');
    setSurvivor(tokenId);
    setPicked(prev => {
      const next = new Set(prev);
      next.delete(tokenId);
      return next;
    });
  };

  const absorb = useMemo(() => [...picked].filter(t => t !== survivor), [picked, survivor]);

  // The drum being split, and what the two halves would come to. A finished
  // drum divides the metres it HOLDS; one that has not been made divides what it
  // is PLANNED for — the same rule the backend picks from the drum's state, so
  // the arithmetic on screen is the arithmetic that will be applied.
  const splitRow = useMemo(
    () => (mode === 'split' && picked.size === 1 ? rows.find(r => r.tokenId === [...picked][0]) : null),
    [mode, picked, rows]
  );
  const splitProduced = splitRow?.status === 'DONE' && Number(splitRow?.producedLength) > 0;
  const splitTotal = splitRow
    ? Number(splitProduced ? splitRow.producedLength : splitRow.plannedLength) || 0
    : 0;
  const keepNum = Number(String(keepLength).replace(',', '.'));
  const splitRest =
    splitTotal > 0 && Number.isFinite(keepNum) && keepNum > 0 && keepNum < splitTotal
      ? Math.round((splitTotal - keepNum) * 1000) / 1000
      : 0;

  // ── HANDOVER ──────────────────────────────────────────────────────────────
  //
  // The case the four verbs above cannot express, and the one that keeps coming
  // off the floor: this drum is credited with metres that are physically on the
  // NEXT drum of the same run.
  //
  // 2026-10-22 on L70 — CYYF 3x2.5, three colours, 10 000 m each. The blue drum
  // was closed by hand at 15 021 m because that is what the counter read: the
  // drum change to BROWN was never registered, so blue's record swallowed the
  // brown bobbin's first 5 021 m. The fix is one sentence — "5 021 of these are
  // brown's" — and there was no way to say it:
  //
  //   · "move to another product" re-points a WHOLE drum and refuses a target
  //     in the same product, so the brown drum — same job, next task — could
  //     never appear in it. Its list was every job in the factory, which is why
  //     it read as hundreds of useless options.
  //   · split cuts the record in two, then asks which PRODUCT the tail belongs
  //     to. Same dead end.
  //
  // So the target here is a DRUM, named token by token, drawn from this
  // machine's own queue — including the one the line is actually on, which the
  // queue only started reporting (`onTheLine`) for this.
  //
  // It is two length corrections, not a new mechanism: `reconcile-lengths`,
  // the same endpoint the reconcile wizard posts. Each side re-derives its task,
  // its material and its inputs' consumption.
  const lineDrums = useMemo(() => lineDrumsOf(queue), [queue]);

  const handSource = useMemo(
    () => (mode === 'handover' && picked.size === 1 ? rows.find(r => r.tokenId === [...picked][0]) : null),
    [mode, picked, rows]
  );

  const handTargets = useMemo(
    () => handoverTargets(lineDrums, handSource, data?.phase),
    [lineDrums, handSource, data]
  );

  const handTarget = handTargets.find(d => d.key === targetDrumKey) || null;

  // `planLengthCorrections` warns when a bobbin's total changes, because on a
  // MERGE execution that means the correction is not a re-slice between the
  // products sharing one drum. A handover is two different bobbins by
  // definition, so each total changing is the whole point — saying it twice as a
  // warning buries the ones that matter (a downstream phase already drew more
  // than the drum would be left with).
  const shownWarnings = useMemo(() => {
    const all = preview?.warnings || [];
    if (mode !== 'handover') return all;
    return all.filter(w => !/is not a re-slice/i.test(String(w)));
  }, [preview, mode]);

  const shownProducts = useMemo(() => {
    const q = productFilter.trim().toLowerCase();
    if (!q) return products;
    return products.filter(p =>
      `${p.productLabel} ${p.plannedLength || ''} ${p.jobId}`.toLowerCase().includes(q)
    );
  }, [products, productFilter]);

  const handSourceHas = Number(handSource?.producedLength) || 0;
  const handOverrun = handSource ? overrunOf(handSource) : 0;
  const handNum = asMetres(handAmount);
  const handIssue = handoverIssue({ source: handSource, target: handTarget, amount: handNum });
  const handOk = Number.isFinite(handNum) && handNum > 0 && handNum <= handSourceHas;

  const ready =
    mode === 'cancel'
      ? picked.size > 0
      : mode === 'merge'
        ? Boolean(survivor) && absorb.length > 0
        : mode === 'split'
          ? Boolean(splitRow) && splitRest > 0
          : mode === 'handover'
            ? !handIssue
            : picked.size === 1 && Boolean(targetJobId);

  // What the merge comes to, computed here so the number on screen is the
  // number the operator is agreeing to before any request is sent.
  const mergedTotal = useMemo(() => {
    if (mode !== 'merge' || !survivor) return null;
    const get = id => rows.find(r => r.tokenId === id);
    return [survivor, ...absorb].reduce((s, id) => s + (Number(get(id)?.producedLength) || 0), 0);
  }, [mode, survivor, absorb, rows]);

  const call = async dryRun => {
    setErr('');
    setBusy(true);
    try {
      // The handover is the one that is not per-job: it re-states a length on
      // each side, and the two drums can belong to two different products.
      const url =
        mode === 'handover'
          ? '/api/redis/jobs/reconcile-lengths'
          : `/api/redis/jobs/${encodeURIComponent(jobId)}/${
              {
                cancel: 'cancel-drums',
                merge: 'merge-drums',
                split: 'split-drum',
                reassign: 'reassign-drum'
              }[mode]
            }`;
      const body =
        mode === 'cancel'
          ? { tokenIds: [...picked], reason, dryRun }
          : mode === 'merge'
            ? { survivor, absorb, reason, dryRun }
            : mode === 'split'
              ? { tokenId: [...picked][0], keepLength: keepNum, reason, dryRun }
              : mode === 'handover'
                ? {
                    items: handoverItems({
                      sourceJobId: jobId,
                      source: handSource,
                      target: handTarget,
                      amount: handNum
                    }),
                    reason: reason || `${n0(handNum)} m handed to ${handTarget.tokenId}`,
                    dryRun
                  }
                : { tokenId: [...picked][0], targetJobId, inputPolicy, reason, dryRun };

      const { data: d } = await http.post(url, body);
      if (dryRun) {
        setPreview(d);
      } else {
        const cleared = d.queueCleared || 0;
        const message =
          mode === 'cancel'
            ? `${(d.removed || []).length} drum(s) removed; ${cleared} queue entr${
                cleared === 1 ? 'y' : 'ies'
              } cleared.`
            : mode === 'merge'
              ? `Merged into ${d.survivor?.tokenId} — ${n0(d.survivor?.producedTo)} m on one bobbin.`
              : mode === 'split'
                ? `${d.source?.tokenId} keeps ${n0(
                    d.mode === 'PRODUCED' ? d.source?.producedTo : d.source?.plannedTo
                  )} m; ${d.created?.tokenId} takes ${n0(d.rest)} m` +
                  `${d.queueAdded ? ', queued behind it' : ''}.` +
                  (d.mode === 'PRODUCED'
                    ? ` Now say which product those ${n0(d.rest)} m belong to.`
                    : '')
                : mode === 'handover'
                ? `${n0(handNum)} m moved onto ${handTarget?.tokenId}` +
                  `${handTarget?.color ? ` (${handTarget.color})` : ''} — ` +
                  `${(d.ops || []).length} drum(s) re-stated.`
                : `${d.move?.tokenId} now belongs to ${d.move?.to || 'the target product'}` +
                  `${d.move?.mode === 'split' ? ', split off its run' : ''}.`;

        setPreview(null);
        // A split almost never ends at the split. The metres were separated out
        // BECAUSE they are a drum of their own, and the next question — whose
        // drum — is the reason anybody came here. So the modal asks it, with the
        // new drum already picked, instead of leaving the planner to find a
        // token id they have never seen before in a list of identical rows.
        //
        // Two writes, not one: each is previewed and audited on its own. A drum
        // that is split and not moved is a visible, correctable state; a single
        // compound write that half-succeeded is not.
        const chainTo = mode === 'split' && d.mode === 'PRODUCED' ? d.created?.tokenId : null;
        await load(chainTo ? { mode: 'reassign', tokenId: chainTo } : null);
        // After the reload: `load` resets, and reset clears this.
        setDone(message);
        onApplied?.();
      }
    } catch (e) {
      const d = e?.response?.data;
      // A refusal is not a failure: the 409 carries the reasons, and they are
      // the most useful thing that can be on this screen.
      if (d?.blockers?.length) setErr(d.blockers.join(' · '));
      else setErr(d?.error || e?.message || 'Failed.');
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <div>
          <Modal.Title className="fs-7">
            Drums of this run
            {data?.phase && (
              <Badge bg="secondary" className="ms-2 fs-10">
                {data.phase}
              </Badge>
            )}
          </Modal.Title>
          <div className="fs-10 text-700">
            {data?.productLabel || jobId}
            {data ? ` · planned ${n0(data.taskPlanned)} m · made ${n0(data.taskExecuted)} m` : ''}
            {machineId ? ` · ${machineId.toUpperCase()}` : ''}
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        <div className="fs-9 text-700 mb-3">
          A drum the machine never wound can be <strong>cancelled</strong>, and drums that
          turned out to be one bobbin can be <strong>merged</strong>. Cancelling gives the
          metres back to the drum this run consumed; merging keeps the total and puts it on
          one drum. One record that is really two bobbins — a drum change the line never
          noticed — is <strong>split</strong>, which keeps the total too. Metres credited to one
          drum that are physically on the next drum of this run are{' '}
          <strong>given to it</strong> — pick the drum, not a product. A drum that is real but
          credited to the wrong product is <strong>moved</strong> — the rest of its run stays
          where it is. Anything downstream has already drawn on is refused, not adjusted.
        </div>

        <div className="d-flex gap-2 mb-3">
          <Button
            size="sm"
            variant={mode === 'cancel' ? 'danger' : 'phoenix-secondary'}
            onClick={() => {
              setMode('cancel');
              reset();
            }}
          >
            <FontAwesomeIcon icon={faBan} className="me-1" />
            Cancel drums
          </Button>
          <Button
            size="sm"
            variant={mode === 'merge' ? 'primary' : 'phoenix-secondary'}
            onClick={() => {
              setMode('merge');
              reset();
            }}
          >
            <FontAwesomeIcon icon={faObjectGroup} className="me-1" />
            Merge into one
          </Button>
          <Button
            size="sm"
            variant={mode === 'split' ? 'info' : 'phoenix-secondary'}
            onClick={() => {
              setMode('split');
              reset();
            }}
            title="One record that is two bobbins — an undetected drum change. Or a drum the operator will wind as two."
          >
            <FontAwesomeIcon icon={faScissors} className="me-1" />
            Split in two
          </Button>
          <Button
            size="sm"
            variant={mode === 'handover' ? 'success' : 'phoenix-secondary'}
            onClick={() => {
              setMode('handover');
              reset();
            }}
            title="Metres credited here that are physically on the next drum of this run — hand them over"
          >
            <FontAwesomeIcon icon={faArrowRightArrowLeft} className="me-1" />
            Give metres to another drum
          </Button>
          <Button
            size="sm"
            variant={mode === 'reassign' ? 'warning' : 'phoenix-secondary'}
            onClick={() => {
              setMode('reassign');
              reset();
            }}
          >
            <FontAwesomeIcon icon={faRightLeft} className="me-1" />
            Move to another product
          </Button>
        </div>

        {err && <Alert variant="danger" className="py-2 fs-9">{err}</Alert>}
        {done && <Alert variant="success" className="py-2 fs-9">{done}</Alert>}

        {/* Merging asks four questions the screen never answered — which drum
            is the second one, whose colour survives, whose wire, and what
            actually changes. Asked from the floor on 2026-10-22, on a run that
            had one drum and therefore nothing to merge at all. */}
        {mode === 'merge' && !loading && (
          rows.length < 2 ? (
            <Alert variant="info" className="py-2 fs-9">
              <div className="fw-semibold mb-1">
                This run has {rows.length === 1 ? 'one drum' : 'no drums'}, so there is nothing
                to merge into it.
              </div>
              <div>
                One physical bobbin is one pass of the machine, so only drums of the{' '}
                <strong>same run</strong> can be merged — the other drums on{' '}
                {String(machineId || 'this line').toUpperCase()} are separate runs, one per
                colour. If this drum is credited with metres that are really on one of them,
                use <strong>Give metres to another drum</strong>.
              </div>
            </Alert>
          ) : (
            <Alert variant="secondary" className="py-2 fs-9">
              <div className="fw-semibold mb-1">What merging does</div>
              <div>
                The drum you keep stays exactly what it is — its{' '}
                <strong>colour</strong>, its wire, its diameter, its place in the queue. Only
                its lengths change: produced and planned become the <strong>sum</strong>, and
                it inherits the absorbed drums&apos; input drums as well as its own. The
                absorbed drums leave the run; the run&apos;s executed length and progress are
                re-derived from what is left.
              </div>
              <div className="mt-1">
                In the records: the surviving execution is re-stated to the full length and the
                absorbed ones are retired — their <strong>operator spans and minutes stop
                being counted</strong>, so only the survivor&apos;s people and time remain.
                Check Operator Performance if different people wound them.
              </div>
            </Alert>
          )
        )}

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />
          </div>
        ) : (
          <Table responsive hover className="phoenix-table border-top border-200 fs-9 mb-0">
            <thead>
              <tr className="text-center">
                <th style={{ width: '8%' }}>{mode === 'merge' ? 'Keep' : ''}</th>
                <th style={{ width: '6%' }} />
                <th className="text-start">Drum</th>
                <th style={{ width: '12%' }}>Status</th>
                <th style={{ width: '13%' }}>Planned</th>
                <th style={{ width: '13%' }}>Made</th>
                <th style={{ width: '24%' }}>&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const blocked = r.blockers?.length ? r.blockers : null;
                const isSurvivor = survivor === r.tokenId;
                const never = !r.producedLength;
                return (
                  <tr
                    key={r.tokenId}
                    style={{
                      verticalAlign: 'middle',
                      textAlign: 'center',
                      opacity: blocked ? 0.55 : 1
                    }}
                  >
                    <td>
                      {mode === 'merge' && (
                        <Form.Check
                          type="radio"
                          name="survivor"
                          checked={isSurvivor}
                          disabled={!!blocked}
                          onChange={() => chooseSurvivor(r.tokenId)}
                          title="The bobbin that stays"
                        />
                      )}
                    </td>
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={picked.has(r.tokenId)}
                        disabled={!!blocked || isSurvivor}
                        onChange={() => toggle(r.tokenId)}
                        title={
                          mode === 'merge'
                            ? 'Absorbed into the kept bobbin'
                            : mode === 'split'
                              ? 'Split this drum in two'
                              : mode === 'reassign'
                                ? 'Move this drum to another product'
                                : 'Remove this drum'
                        }
                      />
                    </td>
                    <td className="text-start">
                      <span className="fw-semibold">{r.tokenId}</span>
                      {r.color && <span className="text-700 ms-2">{r.color}</span>}
                    </td>
                    <td>
                      <Badge bg={r.status === 'DONE' ? 'success' : 'secondary'} className="fs-10">
                        {r.status || '—'}
                      </Badge>
                    </td>
                    <td>{n0(r.plannedLength)}</td>
                    <td className={never ? 'text-danger fw-semibold' : ''}>
                      {never ? 'never wound' : n0(r.producedLength)}
                    </td>
                    <td className="fs-10 text-start text-700">
                      {blocked ? (
                        <>
                          <FontAwesomeIcon
                            icon={faTriangleExclamation}
                            className="text-warning me-1"
                          />
                          {blocked.join(' · ')}
                        </>
                      ) : r.consumedLength ? (
                        `${n0(r.consumedLength)} m already taken`
                      ) : (
                        ''
                      )}
                    </td>
                  </tr>
                );
              })}
              {!rows.length && (
                <tr>
                  <td colSpan={7} className="text-center text-700 py-3">
                    This run has no drums on it.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}

        {(data?.removed || []).length > 0 && (
          <div className="fs-10 text-700 mt-2">
            Already removed from this project:{' '}
            {data.removed.map(r => r.tokenId || r).join(', ')}
          </div>
        )}

        {mergedTotal != null && absorb.length > 0 && (
          <Alert variant="info" className="py-2 fs-9 mt-3 mb-0">
            {survivor} would hold <strong>{n0(mergedTotal)} m</strong>, absorbing{' '}
            {absorb.join(', ')}.
          </Alert>
        )}

        {shownWarnings.length > 0 && (
          <Alert variant="warning" className="py-2 fs-9 mt-3 mb-0">
            {shownWarnings.map((w, i) => (
              <div key={i}>{w}</div>
            ))}
          </Alert>
        )}
        {preview && (
          <Alert variant="secondary" className="py-2 fs-9 mt-2 mb-0">
            {/* The plan rows carry what each drum HELD, not a before/after pair:
                cancelling takes the whole drum away, and merging moves the whole
                drum onto the survivor. The one before/after in the operation is
                the survivor's, and it is stated separately. */}
            {mode === 'merge' && preview.survivor && (
              <div className="fw-semibold mb-1">
                {preview.survivor.tokenId}: {n0(preview.survivor.producedFrom)} →{' '}
                {n0(preview.survivor.producedTo)} m
              </div>
            )}
            {mode === 'handover' && (preview.ops || []).length > 0 && (
              <>
                {preview.ops.map(op => (
                  <div key={`${op.jobId}::${op.tokenId}`} className="fw-semibold">
                    {op.color || op.tokenId}: {n0(op.from)} → {n0(op.to)} m
                    {op.productLabel ? ` · ${op.productLabel}` : ''}
                  </div>
                ))}
                {(preview.rejected || []).map((r, i) => (
                  <div key={i} className="text-danger">
                    {r.tokenId}: {r.reason}
                  </div>
                ))}
              </>
            )}
            {mode === 'reassign' && preview.move && (
              <>
                <div className="fw-semibold mb-1">
                  {preview.move.tokenId} — {n0(preview.move.producedLength)} m —{' '}
                  {preview.move.from} → {preview.move.to}
                </div>
                <div>
                  {preview.move.mode === 'split'
                    ? `Its run keeps ${preview.move.remainingOnSource?.length || 0} other drum(s) on ${preview.move.from}.`
                    : 'Its run carried nothing else, so the whole run moves with it.'}
                </div>
                {preview.inputs?.map(inp => (
                  <div key={inp.tokenId}>
                    {inp.tokenId}:{' '}
                    {inp.wouldGiveBack
                      ? `${n0(inp.wouldGiveBack)} m given back to ${preview.move.from}`
                      : `keeps its ${n0(inp.consumedLength)} m consumed on ${preview.move.from}`}
                  </div>
                ))}
              </>
            )}
            {mode === 'split' && preview.source && (
              <>
                <div className="fw-semibold mb-1">
                  {preview.source.tokenId}:{' '}
                  {preview.mode === 'PRODUCED'
                    ? `${n0(preview.source.producedFrom)} → ${n0(preview.source.producedTo)} m made`
                    : `${n0(preview.source.plannedFrom)} → ${n0(preview.source.plannedTo)} m planned`}
                </div>
                <div>
                  {preview.created?.tokenId} — a new {preview.created?.status} drum of{' '}
                  {n0(preview.rest)} m
                  {preview.created?.drum_diameter
                    ? ` on ${preview.created.drum_material || ''} Ø${preview.created.drum_diameter}`
                    : ''}
                </div>
              </>
            )}
            {mode !== 'reassign' &&
              mode !== 'split' &&
              preview.rows?.map((row, i) => (
                <div key={i}>
                  {row.tokenId}
                  {row.color ? ` (${row.color})` : ''}:{' '}
                  {mode === 'merge'
                    ? `${n0(row.produced)} m moved onto ${preview.survivor?.tokenId}`
                    : row.produced
                      ? `${n0(row.produced)} m given back`
                      : `never wound — ${n0(row.planned)} m planned, removed`}
                </div>
              ))}
            {preview.queueEntries?.length > 0 && mode === 'split' && (
              <div className="mt-1">
                {preview.mode === 'PLANNED'
                  ? `Queues the second drum straight after the first on ${[
                      ...new Set(preview.queueEntries.map(q => q.machineId))
                    ].join(', ')}.`
                  : 'The queue is left alone — both drums are already made.'}
              </div>
            )}
            {preview.queueEntries?.length > 0 && mode !== 'split' && (
              <div className="mt-1">
                {mode === 'reassign' ? 'Re-points' : 'Clears'} {preview.queueEntries.length} queue
                entr{preview.queueEntries.length === 1 ? 'y' : 'ies'} on{' '}
                {[...new Set(preview.queueEntries.map(q => q.machineId))].join(', ')}
                {mode === 'reassign' ? ', keeping its place in the queue' : ''}.
              </div>
            )}
          </Alert>
        )}

        {mode === 'split' && splitRow && (
          <div className="mt-3 p-3 border border-200 rounded">
            <div className="fs-10 text-700 mb-2">
              {splitProduced ? (
                <>
                  <strong>{splitRow.tokenId}</strong> holds {n0(splitTotal)} m. How much of it
                  was the <em>first</em> bobbin? The rest becomes a second finished drum —
                  the total does not change.
                </>
              ) : (
                <>
                  <strong>{splitRow.tokenId}</strong> is planned for {n0(splitTotal)} m and has
                  not been made. How long is the <em>first</em> drum? The rest becomes a
                  second drum, queued straight behind it.
                </>
              )}
            </div>

            <Form.Group>
              <Form.Label className="fs-10 mb-1">
                Metres on the first drum
                {splitProduced && Number(splitRow.plannedLength) > 0 && (
                  <span className="text-700">
                    {' '}
                    · it was planned for {n0(splitRow.plannedLength)} m
                  </span>
                )}
              </Form.Label>
              <div className="d-flex align-items-center gap-2">
                <Form.Control
                  size="sm"
                  style={{ maxWidth: 160 }}
                  value={keepLength}
                  inputMode="decimal"
                  placeholder="e.g. 4050"
                  onChange={e => {
                    setPreview(null);
                    setDone('');
                    setKeepLength(e.target.value);
                  }}
                />
                {/* The two obvious answers, so the common cases are one click.
                    The planned length is what the first drum was SUPPOSED to be,
                    which is the usual truth when a drum change went unnoticed. */}
                {splitProduced && Number(splitRow.plannedLength) > 0 && (
                  <Button
                    size="sm"
                    variant="phoenix-secondary"
                    onClick={() => {
                      setPreview(null);
                      setKeepLength(String(splitRow.plannedLength));
                    }}
                  >
                    as planned ({n0(splitRow.plannedLength)})
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="phoenix-secondary"
                  onClick={() => {
                    setPreview(null);
                    setKeepLength(String(Math.round((splitTotal / 2) * 1000) / 1000));
                  }}
                >
                  half
                </Button>
              </div>
            </Form.Group>

            {splitRest > 0 && (
              <Alert variant="info" className="py-2 fs-9 mt-3 mb-0">
                {splitRow.tokenId} keeps <strong>{n0(keepNum)} m</strong> and a second drum
                takes <strong>{n0(splitRest)} m</strong> — {n0(splitTotal)} m in total, as
                before.
                {splitProduced && (
                  <div className="fs-10 mt-1">
                    The run time and each operator&apos;s share are divided with the length.
                    If the line stood still between the two drums — which is why the change
                    went unnoticed — check Operator Performance afterwards.
                  </div>
                )}
              </Alert>
            )}
            {keepLength !== '' && splitRest <= 0 && (
              <Alert variant="warning" className="py-2 fs-9 mt-3 mb-0">
                The first drum has to be shorter than the {n0(splitTotal)} m on the record,
                or there is no second drum.
              </Alert>
            )}
          </div>
        )}

        {mode === 'handover' && (
          <div className="mt-3 p-3 border border-200 rounded">
            {!handSource ? (
              <div className="fs-9 text-700">
                Pick the drum the metres are credited to, above.
              </div>
            ) : handSourceHas <= 0 ? (
              <Alert variant="warning" className="py-2 fs-9 mb-0">
                {handSource.tokenId} has no metres on it — there is nothing to hand over. A
                drum that was never wound is <strong>cancelled</strong> instead.
              </Alert>
            ) : (
              <>
                <Form.Group className="mb-3">
                  <Form.Label className="fs-10 mb-1">
                    How many of {handSource.tokenId}&apos;s {n0(handSourceHas)} m are not its own
                  </Form.Label>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Control
                      size="sm"
                      style={{ width: 130 }}
                      value={handAmount}
                      onChange={e => {
                        setPreview(null);
                        setHandAmount(e.target.value);
                      }}
                      placeholder="metres"
                    />
                    {handOverrun > 0 && (
                      <Button
                        size="sm"
                        variant="phoenix-secondary"
                        className="fs-10 py-0 px-2"
                        onClick={() => {
                          setPreview(null);
                          setHandAmount(String(handOverrun));
                        }}
                      >
                        the over-run — {n0(handOverrun)} m
                      </Button>
                    )}
                    {handOk && (
                      <span className="fs-10 text-700">
                        leaves {n0(Math.round((handSourceHas - handNum) * 1000) / 1000)} m on it
                      </span>
                    )}
                  </div>
                  {handAmount !== '' && !handOk && (
                    <div className="text-danger fs-10 mt-1">
                      A number between 0 and {n0(handSourceHas)}.
                    </div>
                  )}
                </Form.Group>

                <Form.Group>
                  <Form.Label className="fs-10 mb-1">
                    Which drum they are really on
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={targetDrumKey}
                    onChange={e => {
                      setPreview(null);
                      setTargetDrumKey(e.target.value);
                    }}
                  >
                    <option value="">— choose a drum on {String(machineId || '').toUpperCase()} —</option>
                    {handTargets.map(d => (
                      <option key={d.key} value={d.key}>
                        {d.onTheLine ? '▶ ON THE LINE · ' : ''}
                        {d.color || '—'} · {d.tokenId} · {n0(d.produced)}/{n0(d.planned)} m ·{' '}
                        {d.status}
                        {d.jobId !== String(jobId) ? ` · ${d.product}` : ''}
                      </option>
                    ))}
                  </Form.Select>

                  {!handTargets.length && (
                    <div className="text-warning fs-10 mt-1">
                      {!queue.length
                        ? `Reading ${String(machineId || '').toUpperCase()}'s queue…`
                        : `${String(machineId || '').toUpperCase()} has no other ${
                            data?.phase || 'drum'
                          } drum queued. If the metres belong to a product that has no drum for them, use "Move to another product".`}
                    </div>
                  )}

                  {handTarget && handOk && (
                    <div className="fs-10 mt-2">
                      <div>
                        {handTarget.color || handTarget.tokenId} goes from{' '}
                        <span className="font-monospace">{n0(handTarget.produced)}</span> to{' '}
                        <span className="font-monospace fw-bold">
                          {n0(Math.round((handTarget.produced + handNum) * 1000) / 1000)}
                        </span>{' '}
                        m. Both drums keep their place in the queue; no drum is created or
                        removed.
                      </div>
                      {handTarget.onTheLine && (
                        <div className="text-info mt-1">
                          {String(machineId || '').toUpperCase()} is counting this drum right
                          now — it will write its own figure when it closes it, and these
                          metres are already in that count.
                        </div>
                      )}
                      {handSource.status === 'DONE' && handTarget.status !== 'DONE' && (
                        <div className="text-700 mt-1">
                          The material and the input drums follow the metres on both sides.
                        </div>
                      )}
                    </div>
                  )}
                </Form.Group>
              </>
            )}
          </div>
        )}

        {mode === 'reassign' && (
          <div className="mt-3 p-3 border border-200 rounded">
            <Form.Group className="mb-3">
              <Form.Label className="fs-10 mb-1">Move it to</Form.Label>
              {/* A list of every job in the factory is not a way to find one.
                  The metres usually belong to a drum on this line anyway — that
                  is the HANDOVER above; this is the cross-product case. */}
              <Form.Control
                size="sm"
                className="mb-1"
                value={productFilter}
                onChange={e => setProductFilter(e.target.value)}
                placeholder={`filter ${products.length} products — name, lot or id`}
              />
              <Form.Select
                size="sm"
                value={targetJobId}
                onChange={e => {
                  setPreview(null);
                  setTargetJobId(e.target.value);
                }}
              >
                <option value="">— choose the product it really belongs to —</option>
                {shownProducts.map(p => (
                  <option key={p.jobId} value={p.jobId}>
                    {p.productLabel}
                    {p.plannedLength ? ` · lot ${n0(p.plannedLength)} m` : ''} ·{' '}
                    {p.jobId.slice(-6)}
                  </option>
                ))}
              </Form.Select>
              {productFilter && (
                <div className="fs-10 text-700 mt-1">
                  {shownProducts.length} of {products.length}
                </div>
              )}
            </Form.Group>

            {/* The question the ledger could never answer. A drum made here ate
                metres off an input drum belonging to THIS product; whether that
                was right depends on what physically happened, and only the
                person who was there knows. */}
            <Form.Label className="fs-10 mb-1">
              The drum it was made from belongs to {data?.productLabel || 'this product'}
            </Form.Label>
            <Form.Check
              type="radio"
              name="inputPolicy"
              id="inputPolicy-keep"
              className="fs-9"
              checked={inputPolicy === 'keep'}
              onChange={() => {
                setPreview(null);
                setInputPolicy('keep');
              }}
              label={`${data?.productLabel || 'This product'} really used those metres — leave its input drum alone`}
            />
            <Form.Check
              type="radio"
              name="inputPolicy"
              id="inputPolicy-giveback"
              className="fs-9"
              checked={inputPolicy === 'give-back'}
              onChange={() => {
                setPreview(null);
                setInputPolicy('give-back');
              }}
              label="The drum was only credited here by mistake — give the input drum its metres back"
            />
          </div>
        )}

        <Form.Group className="mt-3">
          <Form.Label className="fs-10 mb-1">
            Why — this goes into the project&apos;s activity log
          </Form.Label>
          <Form.Control
            size="sm"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder={
              mode === 'cancel'
                ? 'e.g. the third drum was never started'
                : mode === 'split'
                  ? 'e.g. pinoles opened, 36 h stop, the drum finish was never detected'
                  : 'e.g. it was one bobbin, the counter reset mid-drum'
            }
          />
        </Form.Group>
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        <div className="fs-10 text-700">
          {mode === 'cancel'
            ? `${picked.size} drum(s) selected`
            : mode === 'merge'
              ? survivor
                ? `keeping ${survivor}, absorbing ${absorb.length}`
                : 'choose the bobbin that stays'
              : mode === 'split'
                ? splitRow
                  ? splitRest > 0
                    ? `${n0(keepNum)} m + ${n0(splitRest)} m`
                    : 'how long was the first drum?'
                  : 'choose the drum that is really two'
                : mode === 'handover'
                  ? handIssue || `${n0(handNum)} m → ${handTarget.color || handTarget.tokenId}`
                  : picked.size === 1
                    ? targetJobId
                      ? `moving ${[...picked][0]}`
                      : 'choose the product it belongs to'
                    : 'choose one drum to move'}
        </div>
        <div className="d-flex gap-2">
          <Button variant="phoenix-secondary" size="sm" onClick={onHide}>
            Close
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            disabled={!ready || busy}
            onClick={() => call(true)}
          >
            {busy ? <Spinner animation="border" size="sm" /> : 'Preview'}
          </Button>
          <Button
            variant={
              mode === 'cancel'
                ? 'danger'
                : mode === 'merge'
                  ? 'primary'
                  : mode === 'split'
                    ? 'info'
                    : 'warning'
            }
            size="sm"
            // Nothing is written until the preview has been seen. Every one of
            // these changes what the factory believes it physically has.
            disabled={!ready || busy || !preview}
            onClick={() => call(false)}
          >
            {mode === 'cancel'
              ? 'Remove drums'
              : mode === 'merge'
                ? 'Merge'
                : mode === 'split'
                  ? 'Split in two'
                  : 'Move drum'}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default DrumSurgeryModal;
